import React, { createContext, useState, useEffect, useCallback, useContext, useMemo } from "react";
import * as api from "../api/index";

// Replaces the old per-component fetch in utils/permissions.js (15+
// components each independently calling getUserPermissions on every mount -
// see the investigation this redesign is based on). One fetch per login,
// shared everywhere via usePermissions() below, with a refresh() the admin
// UI can call after editing a user's own role.
const PermissionsContext = createContext(null);

const FALLBACK_PERMISSIONS = {
  pages: [],
  canApplyCustomDiscount: false,
  canDownloadPdfReports: false,
  canOverridePrice: false,
  isAdmin: false,
};

// A permission/role change made by an admin doesn't push to anyone else's
// already-open session - without this, someone could keep using a role
// they've since been moved off of for as long as their tab stays open. This
// is a background safety net, not the main way permissions load: silent
// polls (below) never flip `loading`, so they can't flicker every disabled
// button in the app every few minutes.
const POLL_INTERVAL_MS = 5 * 60 * 1000;

export const PermissionsProvider = ({ children }) => {
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPermissions = useCallback(async (silent = false) => {
    // This provider wraps the whole app, including the logged-out Login
    // page and public routes, so "no token" is a routine, expected state
    // here - not an error worth logging every single logged-out page view.
    const profile = JSON.parse(localStorage.getItem("profile")) || {};
    if (!profile.token) {
      setPermissions(null);
      setError(null);
      setLoading(false);
      return;
    }

    if (!silent) setLoading(true);
    try {
      const { data } = await api.getUserPermissions();
      setPermissions(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching permissions:", err.message);
      // A silent background poll failing (e.g. a dropped connection) is not
      // grounds to strip an already-loaded, working session of its access -
      // only the real initial load falls back to zero permissions.
      if (!silent) setPermissions(FALLBACK_PERMISSIONS);
      setError(err.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPermissions();
    const intervalId = setInterval(() => fetchPermissions(true), POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [fetchPermissions]);

  // Same reasoning as NavDrawerContext: without this, every one of the
  // 15+ components reading usePermissions() re-rendered on every provider
  // render (e.g. each silent poll tick), not just on an actual permissions
  // change.
  const value = useMemo(
    () => ({ permissions, loading, error, refreshPermissions: fetchPermissions }),
    [permissions, loading, error, fetchPermissions]
  );

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissionsContext = () => useContext(PermissionsContext);
